import { defineStore } from 'pinia';
import { useStoreRoot } from '~/stores/abstract/root';
import {
  EBonusStatus,
  EBonusType,
  type IBonusData,
  type IBonusDataFromStrapi,
  type IBonusesPageDataStrapiResponse,
  type IBonusesStrapiResponse,
} from '~/types/bonuses';
import { getNextDayOfTheWeek } from '~/utils/date/helpers';
import {
  createPopulateString,
  parseItemInCollection,
} from '~/utils/strapiComponents/addStrapiData';
import {
  filterDateActiveItem,
  findIfPassesByGroups,
  findIfPassesByUsers,
} from '~/utils/strapiComponents/filters';
import { useStoreCrmBonus } from '../backend/crm-bonus';
import { useStoreSsPlayer } from '../softswiss/player';
import { strapi } from '../transport/strapi';
import { useStoreBonusesBanners } from './bonusesBanners';
import { useStoreBonusesTabs } from './bonusesTabs';

export const useStoreBonuses = defineStore('bonuses', {
  state: () => ({
    bonuses: [] as IBonusData[],
    activeDepositBonus: null as IBonusData | null,
    requestPromise: null as Promise<IBonusesStrapiResponse> | null,
    requestPageBonusesPromise: null as Promise<IBonusesPageDataStrapiResponse> | null,
  }),

  getters: {
    activeBonuses(state): IBonusData[] {
      const storeRoot = useStoreRoot();
      const storeSsPlayer = useStoreSsPlayer();

      const activeBonuses = state.bonuses
        // get bonuses with selected language
        .filter((bonus) => bonus.locale === storeRoot.userLang)

        // filter start and finish date
        .filter(filterDateActiveItem)

        .filter((bonus) => {
          return findIfPassesByUsers(bonus?.showForUsers || '');
        })

        // filter depending on hide for groups or for guests
        .filter((bonus) => {
          if (storeSsPlayer.isPlayerAuthorised) {
            return findIfPassesByGroups(
              bonus.groupFilter?.includedGroups || '',
              bonus.groupFilter?.excludedGroups || ''
            );
          } else {
            return !bonus.groupFilter?.hideForGuests;
          }
        });

      return activeBonuses;
    },
    welcomePackBonusesWithStatuses(): IBonusData[] {
      const storeSsPlayer = useStoreSsPlayer();
      const welcomePackBonusesWithStatuses = [] as IBonusData[];
      const welcomePackBonuses = this.activeBonuses.filter(
        (bonus) => bonus.bonusType === EBonusType.Welcome
      );

      if (!storeSsPlayer.isPlayerAuthorised) {
        return welcomePackBonuses;
      }

      welcomePackBonuses.forEach((bonus) => {
        storeSsPlayer.playerInfo.statuses?.find((status) => {
          if (bonus.groupRecieved && status.id === bonus.groupRecieved) {
            welcomePackBonusesWithStatuses.push({ ...bonus, status: EBonusStatus.done });
            return true;
          } else if (bonus.groupLocked && status.id === bonus.groupLocked) {
            welcomePackBonusesWithStatuses.push({ ...bonus, status: EBonusStatus.locked });
            return true;
          } else if (bonus.groupMissed && status.id === bonus.groupMissed) {
            welcomePackBonusesWithStatuses.push({ ...bonus, status: EBonusStatus.missed });
            return true;
          } else if (!bonus.groupActive || status.id === bonus.groupActive) {
            welcomePackBonusesWithStatuses.push({ ...bonus, status: EBonusStatus.active });
            return true;
          } else {
            return false;
          }
        });
      });

      return welcomePackBonusesWithStatuses;
    },
    isHideWelcomeBonuses(): boolean {
      const storeSsPlayer = useStoreSsPlayer();
      const isHideWelcomeBonuses =
        storeSsPlayer.isPlayerAuthorised &&
        this.welcomePackBonusesWithStatuses.every(
          (bonus) => bonus.status === EBonusStatus.done || bonus.status === EBonusStatus.missed
        );
      if (isHideWelcomeBonuses) {
        return true;
      }
      return false;
    },

    promotionBonusesWithStatuses(): IBonusData[] {
      const storeSsPlayer = useStoreSsPlayer();
      const storeCrmBonus = useStoreCrmBonus();
      const promotionBonuses = this.activeBonuses.filter(
        (bonus) => bonus.bonusType !== EBonusType.Welcome
      );

      const findClosestActiveDay = (days?: string | null) => {
        if (!days) {
          return null;
        }
        const day = new Date().getDay();
        const daysSorted = days
          .split(',')
          .map((el) => Number(el))
          .sort((a, b) => a - b);
        const activeDay = daysSorted.find((el) => el > day);
        return activeDay || daysSorted[0];
      };

      return promotionBonuses.map((bonus) => {
        const day = new Date().getDay().toString();
        const closestActiveDay =
          bonus.includedDays?.length === 1
            ? Number(bonus.includedDays)
            : findClosestActiveDay(bonus.includedDays);
        const closestInactiveDay =
          bonus.excludedDays?.length === 1
            ? Number(bonus.excludedDays)
            : findClosestActiveDay(bonus.excludedDays);

        if (bonus.crmAutoCampaignId && storeCrmBonus.recievedIds.includes(bonus.id)) {
          return {
            ...bonus,
            status: EBonusStatus.done,
          };
        }

        if (storeSsPlayer.playerInfo.statuses) {
          for (let i = 0; i < storeSsPlayer.playerInfo.statuses.length; i++) {
            const status = storeSsPlayer.playerInfo.statuses[i];
            if (bonus.groupRecieved && status.id === bonus.groupRecieved) {
              return {
                ...bonus,
                status: EBonusStatus.done,
                nextActiveDate: getNextDayOfTheWeek(closestActiveDay),
              };
            } else if (bonus.groupLocked && status.id === bonus.groupLocked) {
              return {
                ...bonus,
                status: EBonusStatus.locked,
                nextActiveDate: getNextDayOfTheWeek(closestActiveDay),
              };
            } else if (bonus.groupMissed && status.id === bonus.groupMissed) {
              return {
                ...bonus,
                status: EBonusStatus.missed,
                nextActiveDate: getNextDayOfTheWeek(closestActiveDay),
              };
            }
          }
        }

        if (bonus.includedDays?.length) {
          if (!bonus.includedDays.includes(day)) {
            return {
              ...bonus,
              status: EBonusStatus.locked,
              nextActiveDate: getNextDayOfTheWeek(closestActiveDay),
            };
          } else {
            return {
              ...bonus,
              availableUntil: getNextDayOfTheWeek(closestInactiveDay),
            };
          }
        }
        if (bonus.excludedDays?.length) {
          if (bonus.excludedDays.includes(day)) {
            return {
              ...bonus,
              status: EBonusStatus.locked,
              nextActiveDate: getNextDayOfTheWeek(closestActiveDay),
            };
          }
        }
        return bonus;
      });
    },
    allBonusesWithStatuses(): IBonusData[] {
      if (this.isHideWelcomeBonuses) {
        return this.promotionBonusesWithStatuses;
      }
      return [...this.welcomePackBonusesWithStatuses, ...this.promotionBonusesWithStatuses];
    },
    bonusesWithStatusesWithoutAchievements(): IBonusData[] {
      return this.allBonusesWithStatuses.filter(
        (bonus) => bonus.bonusType !== EBonusType.Achievements
      );
    },
    promotionBonusesByType() {
      const promotionsByType = {} as { [key: string]: IBonusData[] };

      this.allBonusesWithStatuses.forEach((bonus) => {
        const current = promotionsByType[bonus.bonusType];
        if (current) {
          promotionsByType[bonus.bonusType].push(bonus);
        } else {
          promotionsByType[bonus.bonusType] = [bonus];
        }
      });

      return promotionsByType;
    },
    depositAvailableBonuses(): IBonusData[] {
      const notWelcomeBonuses = this.promotionBonusesWithStatuses.filter(
        (bonus) => !bonus.status || bonus.status === 'active'
      );

      const welcomeBonus = this.welcomePackBonusesWithStatuses.filter(
        (item) => item.status === 'active'
      );

      return [...welcomeBonus, ...notWelcomeBonuses]
        .filter((bonus) => {
          if ((bonus.bonusCode && !bonus.linkAction) || Boolean(bonus.showInCashierModal)) {
            return true;
          }
          return false;
        })
        .filter((bonus) => {
          const day = new Date().getDay().toString();

          if (bonus?.excludedDays?.length) {
            if (bonus.excludedDays.includes(day)) {
              return false;
            }

            return true;
          }

          if (bonus?.includedDays?.length) {
            if (bonus.includedDays.includes(day)) {
              return true;
            }

            return false;
          }

          return true;
        });
    },
    gamePageBonuses(): IBonusData[] {
      const isPlayerAuthorised = useStoreSsPlayer().isPlayerAuthorised;
      const notWelcomeBonuses = this.promotionBonusesWithStatuses.filter((bonus) =>
        isPlayerAuthorised ? !bonus.status || bonus.status === 'active' : true
      );

      const welcomeBonus = isPlayerAuthorised
        ? this.welcomePackBonusesWithStatuses.filter((item) => item.status === 'active')
        : this.welcomePackBonusesWithStatuses.filter((item) => item.position === 1);

      return [...welcomeBonus, ...notWelcomeBonuses]
        .filter(
          (bonus) =>
            bonus.bonusType !== EBonusType.Telegram && bonus.bonusType !== EBonusType.Cashback
        )
        .filter((bonus) => {
          if (!isPlayerAuthorised) {
            return true;
          }
          const day = new Date().getDay().toString();

          if (bonus?.excludedDays?.length) {
            if (bonus.excludedDays.includes(day)) {
              return false;
            }

            return true;
          }

          if (bonus?.includedDays?.length) {
            if (bonus.includedDays.includes(day)) {
              return true;
            }

            return false;
          }

          return true;
        });
    },
  },

  actions: {
    async loadBonusesData() {
      if (!this.requestPromise) {
        const populateString = createPopulateString([
          'bonusType',
          'recievedBonusCustomIcon',
          'lockedBonusCustomIcon',
          'groupFilter',
          'cashierBonusBanner',
          'missedBonusCustomIcon',
          'bannerCardFront',
          'bannerCardBack',
          'tablesWithHeading',
          'tablesWithHeading.tableItem',
          'linkAction',
          'linkAction.params',
        ]);

        this.requestPromise = strapi()
          .get('bonuses', populateString)
          .finally(() => (this.requestPromise = null));
      }

      const response = await this.requestPromise;

      if (response?.data) {
        const parsedData = response.data.map((tab) =>
          parseItemInCollection({
            ...tab,
            bonusType: tab.bonusType.bonusType,
          })
        );
        const filteredState = this.bonuses.filter(
          (item) => !parsedData.find(({ id }) => id && id === item.id)
        );

        this.bonuses = [...filteredState, ...parsedData].sort((a, b) => a.position - b.position);
        return true;
      } else {
        return false;
      }
    },
    SET_BONUSES_DATA(data: IBonusDataFromStrapi[]) {
      const parsedData = data.map((tab) =>
        parseItemInCollection({
          ...tab,
          bonusType: tab.bonusType.bonusType,
        })
      );
      const filteredState = this.bonuses.filter(
        (item) => !parsedData.find(({ id }) => id && id === item.id)
      );

      this.bonuses = [...filteredState, ...parsedData].sort((a, b) => a.position - b.position);
    },
    setActiveDepositBonus(bonus: IBonusData) {
      this.activeDepositBonus = bonus;
    },
    removeActiveDepositBonus() {
      this.activeDepositBonus = null;
    },
    async loadBonusesPageData() {
      if (!this.requestPageBonusesPromise) {
        this.requestPageBonusesPromise = await strapi()
          .get('bonuses-data')
          .finally(() => (this.requestPageBonusesPromise = null));
      }

      const response = await this.requestPageBonusesPromise;
      if (response) {
        this.SET_BONUSES_DATA(response.bonusesData);
        useStoreBonusesTabs().SET_BONUSES_TABS(response.bonusesTabs);
        useStoreBonusesBanners().SET_BONUSES_BANNERS(response.bonusesBanners);
      }

      return true;
    },
  },
});
